import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { photoUrl } from "@/lib/api";
import {
  ArrowLeft,
  Plus,
  Settings2,
  Trash2,
  Factory,
  ChevronRight,
  Loader2,
  Pencil,
  Sparkles,
  FileSpreadsheet,
  Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import ProductTypeSchemaSheet from "@/components/ProductTypeSchemaSheet";
import ProductTable from "@/components/ProductTable";
import ManufacturerFormDialog from "@/components/ManufacturerFormDialog";
import SpecImportSheet from "@/components/SpecImportSheet";
import CsvNewPtSheet from "@/components/CsvNewPtSheet";
import ManufacturerAgenciesSection from "@/components/ManufacturerAgenciesSection";
import ManufacturerProductSearch from "@/components/ManufacturerProductSearch";
import ProductTypeSidebar from "@/components/ProductTypeSidebar";
import PromoBannersSection from "@/components/PromoBannersSection";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

export default function ManufacturerDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { refresh: refreshCart } = useCart();
  const [manufacturer, setManufacturer] = useState(null);
  const [productTypes, setProductTypes] = useState([]);
  const [activePtId, setActivePtId] = useState(null);
  const [createPtOpen, setCreatePtOpen] = useState(false);
  const [editPt, setEditPt] = useState(null); // pt object when editing
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteMfr, setConfirmDeleteMfr] = useState(false);
  const [editMfrOpen, setEditMfrOpen] = useState(false);
  const [specImportOpen, setSpecImportOpen] = useState(false);
  const [specImportPtId, setSpecImportPtId] = useState(null);
  const [specImportPtName, setSpecImportPtName] = useState(null);
  const [specImportMode, setSpecImportMode] = useState("auto"); // "auto" | "refine"
  const [csvNewPtOpen, setCsvNewPtOpen] = useState(false);
  const [duplicatingPt, setDuplicatingPt] = useState(false);
  // CSV-data delete confirmation. Stores the PT being wiped + the chosen mode.
  const [deleteCsvFor, setDeleteCsvFor] = useState(null);
  const [deleteCsvMode, setDeleteCsvMode] = useState("clear_columns"); // "clear_columns" | "keep_columns"
  const [csvDeleting, setCsvDeleting] = useState(false);
  // When the search dropdown lands on a product, surface its id so the
  // ProductTable can scroll-into-view and pulse-highlight that row.
  const [highlightProductId, setHighlightProductId] = useState(null);

  const handleSearchPick = (p) => {
    if (!p) return;
    if (p.product_type_id) {
      setActivePtId(p.product_type_id);
    }
    setHighlightProductId(p.id);
    // Clear the highlight after a few seconds so re-clicking the same row works
    setTimeout(() => setHighlightProductId(null), 4000);
  };

  const loadAll = async () => {
    const m = await api.get(`/manufacturers/${id}`);
    setManufacturer(m.data);
    const pts = await api.get(`/manufacturers/${id}/product-types`);
    setProductTypes(pts.data);
    if (pts.data.length > 0 && !pts.data.find((p) => p.id === activePtId)) {
      setActivePtId(pts.data[0].id);
    }
    if (pts.data.length === 0) setActivePtId(null);
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const activePt = useMemo(
    () => productTypes.find((p) => p.id === activePtId) || null,
    [productTypes, activePtId]
  );

  const onCreatedPt = (newPt) => {
    setProductTypes((prev) => [...prev, newPt]);
    setActivePtId(newPt.id);
    setCreatePtOpen(false);
    loadAll();
  };

  const onUpdatedPt = (updated) => {
    setProductTypes((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    setEditPt(null);
  };

  const onDeletePt = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/product-types/${confirmDelete.id}`);
      toast.success("Product type deleted");
      setConfirmDelete(null);
      const next = productTypes.filter((p) => p.id !== confirmDelete.id);
      setProductTypes(next);
      if (next.length > 0) setActivePtId(next[0].id);
      else setActivePtId(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDeleteMfr = async () => {
    try {
      await api.delete(`/manufacturers/${id}`);
      toast.success("Manufacturer deleted");
      window.location.href = "/manufacturers";
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!manufacturer) return <div className="byrd-progress" />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Link
            to="/manufacturers"
            className="inline-flex items-center text-xs uppercase tracking-widest text-muted-foreground hover:text-slate-900 font-mono-data"
            data-testid="back-to-manufacturers"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Manufacturers
          </Link>
          <div className="mt-2 flex items-center gap-4">
            <div className="w-14 h-14 border border-border rounded-sm flex items-center justify-center bg-slate-50 overflow-hidden shrink-0">
              {manufacturer.logo_photo_id ? (
                <img
                  src={photoUrl(manufacturer.logo_photo_id)}
                  alt={manufacturer.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <Factory className="w-6 h-6 text-slate-500" />
              )}
            </div>
            <div className="min-w-0">
              <h1 className="text-3xl font-display font-bold tracking-tight text-slate-900 truncate">
                {manufacturer.name}
              </h1>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data mt-1">
                {manufacturer.product_type_count} product types ·{" "}
                {manufacturer.product_count} products
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setCsvNewPtOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
            data-testid="new-pt-from-csv-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> New from CSV
          </Button>
          <Button
            onClick={() => {
              setSpecImportPtId(null);
              setSpecImportPtName(null);
              setSpecImportMode("auto");
              setSpecImportOpen(true);
            }}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-sm"
            data-testid="import-spec-sheet-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Import from spec sheet
          </Button>
          <Button
            variant="outline"
            onClick={() => setEditMfrOpen(true)}
            className="rounded-sm"
            data-testid="edit-manufacturer-btn"
          >
            <Pencil className="w-4 h-4 mr-2" /> Edit details
          </Button>
          {user?.role === "admin" && (
            <Button
              variant="outline"
              onClick={() => setConfirmDeleteMfr(true)}
              className="rounded-sm text-destructive border-destructive/30 hover:bg-red-50 hover:text-destructive"
              data-testid="delete-manufacturer-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* Rep agency assignments (admin manages, everyone sees) */}
      <ManufacturerAgenciesSection manufacturerId={manufacturer.id} />

      {/* Promo banners (admin only) */}
      <PromoBannersSection
        manufacturerId={manufacturer.id}
        canManage={user?.role === "admin"}
      />

      {/* Catalog-wide product search (admin/agency workflow helper) */}
      {productTypes.length > 0 && (
        <div className="bg-white border border-border rounded-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="font-display text-sm font-semibold tracking-tight">
                Find a product
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Search across every product type for this manufacturer to
                check if a SKU has already been added.
              </p>
            </div>
          </div>
          <ManufacturerProductSearch
            manufacturerId={manufacturer.id}
            onPick={handleSearchPick}
          />
        </div>
      )}

      {/* Product types — searchable sidebar + active type pane */}
      <div className="bg-white border border-border rounded-sm">
        <div className="flex flex-col md:flex-row">
          {/* Left sidebar: searchable product-type list */}
          <ProductTypeSidebar
            productTypes={productTypes}
            activePtId={activePtId}
            onSelect={setActivePtId}
            onAdd={() => setCreatePtOpen(true)}
          />

          {/* Right pane: active product type detail */}
          <div className="flex-1 min-w-0 md:border-l md:border-border">
        {!activePt ? (
          <div className="p-12 text-center">
            <h3 className="font-display text-lg font-semibold text-slate-900">
              No product types yet
            </h3>
            <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
              Define a product type (e.g. "Area Light", "Wire Connector") and
              the custom spec columns it should track. The columns you choose
              power the spreadsheet for that type.
            </p>
            <Button
              onClick={() => setCreatePtOpen(true)}
              className="mt-4 bg-slate-900 hover:bg-slate-800 rounded-sm"
            >
              <Plus className="w-4 h-4 mr-2" /> Add product type
            </Button>
          </div>
        ) : (
          <div className="p-4 md:p-5">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
                  Product type
                  <ChevronRight className="w-3 h-3" />
                  <span className="text-slate-900">{activePt.name}</span>
                </div>
                {activePt.description ? (
                  <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
                    {activePt.description}
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-700"
                  onClick={() => {
                    setSpecImportPtId(activePt.id);
                    setSpecImportPtName(activePt.name);
                    setSpecImportMode("refine");
                    setSpecImportOpen(true);
                  }}
                  data-testid="refine-with-ai-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Refine with AI
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-700"
                  onClick={() => {
                    setSpecImportPtId(activePt.id);
                    setSpecImportPtName(activePt.name);
                    setSpecImportMode("auto");
                    setSpecImportOpen(true);
                  }}
                  data-testid="import-rows-from-spec-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Add via spec sheet
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm"
                  onClick={() => setEditPt(activePt)}
                  data-testid="edit-schema-btn"
                >
                  <Settings2 className="w-4 h-4 mr-2" /> Edit settings
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm"
                  disabled={duplicatingPt}
                  onClick={async () => {
                    if (!activePt) return;
                    setDuplicatingPt(true);
                    try {
                      const { data } = await api.post(
                        `/product-types/${activePt.id}/duplicate`
                      );
                      toast.success(
                        `Duplicated "${activePt.name}" — fill in the new Name + PDF`
                      );
                      await loadAll();
                      setActivePtId(data.id);
                      setEditPt(data);
                    } catch (err) {
                      toast.error(formatApiError(err));
                    } finally {
                      setDuplicatingPt(false);
                    }
                  }}
                  data-testid="duplicate-pt-btn"
                >
                  {duplicatingPt ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Copy className="w-4 h-4 mr-2" />
                  )}
                  Duplicate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm text-amber-700 border-amber-300 hover:bg-amber-50"
                  onClick={() => setDeleteCsvFor(activePt)}
                  data-testid="delete-csv-btn"
                >
                  Delete CSV data
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm text-destructive border-destructive/30 hover:bg-red-50 hover:text-destructive"
                  onClick={() => setConfirmDelete(activePt)}
                  data-testid="delete-product-type-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <ProductTable
              productType={activePt}
              onAfterChange={loadAll}
              highlightProductId={highlightProductId}
            />
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Create / Edit product type schema drawer */}
      <ProductTypeSchemaSheet
        open={createPtOpen}
        onOpenChange={setCreatePtOpen}
        mode="create"
        manufacturerId={id}
        onSaved={onCreatedPt}
      />
      <ProductTypeSchemaSheet
        open={!!editPt}
        onOpenChange={(o) => !o && setEditPt(null)}
        mode="edit"
        productType={editPt}
        onSaved={onUpdatedPt}
      />

      {/* Edit manufacturer dialog */}
      <ManufacturerFormDialog
        open={editMfrOpen}
        onOpenChange={setEditMfrOpen}
        mode="edit"
        manufacturer={manufacturer}
        onSaved={(updated) => setManufacturer((prev) => ({ ...prev, ...updated }))}
      />

      {/* AI spec-sheet import */}
      <SpecImportSheet
        open={specImportOpen}
        onOpenChange={setSpecImportOpen}
        manufacturerId={id}
        manufacturerName={manufacturer.name}
        productTypeId={specImportPtId}
        productTypeName={specImportPtName}
        mode={specImportMode}
        onApplied={(result) => {
          loadAll();
          if (result?.product_type?.id) {
            setActivePtId(result.product_type.id);
          }
        }}
      />

      {/* New product type from a CSV */}
      <CsvNewPtSheet
        open={csvNewPtOpen}
        onOpenChange={setCsvNewPtOpen}
        manufacturerId={id}
        manufacturerName={manufacturer.name}
        onCreated={(pt) => {
          loadAll();
          if (pt?.id) setActivePtId(pt.id);
        }}
      />


      {/* Confirm delete product type */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this product type?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove "{confirmDelete?.name}" and all of its products. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeletePt}
              className="bg-destructive hover:bg-red-600 text-white rounded-sm"
              data-testid="confirm-delete-pt"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm delete manufacturer */}
      <AlertDialog open={confirmDeleteMfr} onOpenChange={setConfirmDeleteMfr}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete manufacturer?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{manufacturer.name}", all of its
              product types, and all of its products. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteMfr}
              className="bg-destructive hover:bg-red-600 text-white rounded-sm"
              data-testid="confirm-delete-mfr"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Delete CSV data dialog — keep/clear column schema */}
      <Dialog
        open={!!deleteCsvFor}
        onOpenChange={(o) => {
          if (!o) {
            setDeleteCsvFor(null);
            setDeleteCsvMode("clear_columns");
          }
        }}
      >
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle>Delete CSV data?</DialogTitle>
            <DialogDescription>
              This wipes the spreadsheet rows for{" "}
              <span className="font-semibold">{deleteCsvFor?.name}</span>. The
              product type itself (name, aliases, default PDF) stays intact.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {[
              {
                value: "clear_columns",
                label: "Full reset",
                hint: "Delete all rows AND clear the column schema. The product type goes back to PDF-only mode.",
              },
              {
                value: "keep_columns",
                label: "Wipe rows only",
                hint: "Keep the column schema so you can re-import a fresh CSV later.",
              },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDeleteCsvMode(opt.value)}
                className={`w-full text-left rounded-sm border px-3 py-2 transition-colors ${
                  deleteCsvMode === opt.value
                    ? "border-amber-500 bg-amber-50 text-slate-900"
                    : "border-border bg-white hover:bg-slate-50"
                }`}
                data-testid={`delete-csv-${opt.value}`}
              >
                <div className="text-sm font-semibold">{opt.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {opt.hint}
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteCsvFor(null)}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={csvDeleting}
              onClick={async () => {
                if (!deleteCsvFor) return;
                setCsvDeleting(true);
                try {
                  await api.delete(
                    `/product-types/${deleteCsvFor.id}/csv-data`,
                    {
                      params: {
                        keep_columns: deleteCsvMode === "keep_columns",
                      },
                    }
                  );
                  toast.success("CSV data deleted");
                  setDeleteCsvFor(null);
                  setDeleteCsvMode("clear_columns");
                  loadAll();
                  // Refresh cart so any phantom badge from now-deleted products clears.
                  refreshCart?.();
                } catch (err) {
                  toast.error(formatApiError(err));
                } finally {
                  setCsvDeleting(false);
                }
              }}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-sm"
              data-testid="confirm-delete-csv"
            >
              {csvDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete CSV data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
